import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAniList } from '../context/AniListContext';
import { updateAnimeStatus, addAnimeWithRetry } from '../services/aniListService';
import { useAlert } from '../context/ToastContext';
import { getMediaStatusBadge } from '../utils/timeFormatter';
import { useAdultContent } from '../context/AdultContentContext';
import { searchAnime as serverSearchAnime, checkServerHealth } from '../services/apiService';
import { useTitlePreference } from '../context/TitlePreferenceContext';
import { getDisplayTitle } from '../hooks/useDisplayTitle';
import CustomSelect from './common/CustomSelect';
import StatusButton from './StatusButton';
import './Search.css';
import './Dashboard.css'; // Import Dashboard CSS for card styling



// AddToListButton component for anime not in user's list
const AddToListButton = ({ onAdd, isAdding, onOpenChange }) => {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = React.useRef(null);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen]);

    // Notify parent when dropdown state changes
    useEffect(() => {
        if (onOpenChange) {
            onOpenChange(isOpen);
        }
    }, [isOpen, onOpenChange]);

    const addOptions = [
        { value: 'PLANNING', label: 'Add to Planning', emoji: '📅', color: '#FF9800' },
        { value: 'CURRENT', label: 'Add to Watching', emoji: '▶️', color: '#4CAF50' },
        { value: 'COMPLETED', label: 'Add to Completed', emoji: '✅', color: '#2196F3' }
    ];

    const handleOptionClick = (e, option) => {
        e.stopPropagation();
        onAdd(option.value);
        setIsOpen(false);
    };

    return (
        <div className="add-to-list-wrapper" ref={dropdownRef}>
            <button
                className={`add-to-list-button ${isAdding ? 'loading' : ''}`}
                onClick={(e) => {
                    e.stopPropagation();
                    if (!isAdding) setIsOpen(!isOpen);
                }}
                disabled={isAdding}
                title="Add to list"
            >
                <span className="add-to-list-emoji">➕</span>
                <span className="add-to-list-label">{isAdding ? 'Adding...' : 'Add to List'}</span>
                <span className="add-to-list-arrow">{isOpen ? '▲' : '▼'}</span>
            </button>

            {isOpen && (
                <div className="add-to-list-dropdown">
                    {addOptions.map(option => (
                        <button
                            key={option.value}
                            className="add-to-list-option"
                            onClick={(e) => handleOptionClick(e, option)}
                            style={{ borderLeftColor: option.color }}
                        >
                            <span className="add-option-emoji">{option.emoji}</span>
                            <span className="add-option-label">{option.label}</span>
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};

const Search = () => {
    const { userData, refreshData, token } = useAniList();
    const { showError } = useAlert();
    const { hideAdultContent } = useAdultContent();
    const [searchQuery, setSearchQuery] = useState('');
    const [filters, setFilters] = useState({
        format: '',
        status: '',
        season: '',
        year: ''
    });
    const [sortBy, setSortBy] = useState('none');
    const [sortDirection, setSortDirection] = useState('desc');
    const [selectedAnime, setSelectedAnime] = useState(null);
    const [userAnimeMap, setUserAnimeMap] = useState(new Map()); // Map of anime ID -> status
    const [optimisticUpdates, setOptimisticUpdates] = useState(new Map());
    const [changingStatus, setChangingStatus] = useState(new Set());
    const [addingToList, setAddingToList] = useState(new Set());
    const [dropdownOpenAnimeId, setDropdownOpenAnimeId] = useState(null); // Track which card has dropdown open

    // Sort options configuration (for CustomSelect - no placeholder option like others)
    const sortOptions = [
        { value: 'name', label: 'Name', icon: '📝' },
        { value: 'score', label: 'Score', icon: '⭐' }
    ];
    const searchTimerRef = useRef(null);
    const abortControllerRef = useRef(null);           // Cancels in-flight HTTP requests
    const searchVersionRef = useRef(0);                 // Guards against stale response races
    const [results, setResults] = useState([]);
    const [isSearching, setIsSearching] = useState(false);
    const [hasSearched, setHasSearched] = useState(false);
    const [serverReady, setServerReady] = useState(true);

    const { titlePreference } = useTitlePreference();
    // Reactive title getter (respects Settings -> Title Language)
    const getPreferredTitle = (anime) => getDisplayTitle(anime, titlePreference);

    // Check server health and load available years on mount
    useEffect(() => {
        const init = async () => {
            try {
                const health = await checkServerHealth();
                if (!health || !health.anime_count) {
                    setServerReady(false);
                    return;
                }
                setServerReady(true);
            } catch (err) {
                console.error('Server health check failed:', err);
                setServerReady(false);
            }
        };
        init();
    }, []);

    // Debounced search function - queries server API
    // Accepts an AbortSignal and version ID for race-condition safety
    const performSearch = useCallback(async (query, currentFilters, signal, version) => {
        const trimmed = query.trim();
        const hasQuery = trimmed.length >= 1; // Min 1 char for text search
        const hasFilters = currentFilters.format || currentFilters.status || currentFilters.season || currentFilters.year;

        if (!hasQuery && !hasFilters) {
            setResults([]);
            setHasSearched(true);
            return;
        }

        // Only fire text search if 1+ chars, but still allow filter-only searches
        if (trimmed.length > 0 && trimmed.length < 1 && !hasFilters) {
            return; // Wait for more chars
        }

        setIsSearching(true);
        try {
            const serverResults = await serverSearchAnime(
                trimmed,
                {
                    format: currentFilters.format || undefined,
                    status: currentFilters.status || undefined,
                    season: currentFilters.season || undefined,
                    year: currentFilters.year || undefined
                },
                100,
                signal
            );

            // Only apply results if this is still the latest request
            if (version === searchVersionRef.current) {
                const filtered = hideAdultContent
                    ? serverResults.filter(item => !item.isAdult)
                    : serverResults;
                setResults(filtered);
                setIsSearching(false);
                setHasSearched(true);
            }
        } catch (error) {
            // Silently ignore cancelled requests
            if (error.name === 'AbortError' || error.name === 'CanceledError') return;
            console.error('Search failed:', error);
            if (version === searchVersionRef.current) {
                setResults([]);
                setIsSearching(false);
                setHasSearched(true);
            }
        }
    }, [hideAdultContent]);

    // Trigger search with debounce on query/filter changes
    useEffect(() => {
        // Clear any pending debounce timer
        if (searchTimerRef.current) {
            clearTimeout(searchTimerRef.current);
        }

        // Abort any in-flight request from the previous keystroke
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }

        // Bump the version counter so any still-pending response is ignored
        searchVersionRef.current += 1;
        const currentVersion = searchVersionRef.current;

        searchTimerRef.current = setTimeout(() => {
            const controller = new AbortController();
            abortControllerRef.current = controller;
            performSearch(searchQuery, filters, controller.signal, currentVersion);
        }, 450); // 450ms debounce — smooth balance of speed vs. request volume

        return () => {
            if (searchTimerRef.current) {
                clearTimeout(searchTimerRef.current);
            }
        };
    }, [searchQuery, filters, performSearch]);

    // Build user anime map from userData
    useEffect(() => {
        if (!userData || !userData.lists) return;

        const animeMap = new Map();
        userData.lists.forEach(list => {
            list.entries.forEach(entry => {
                animeMap.set(entry.media.id, entry.status);
            });
        });
        setUserAnimeMap(animeMap);
    }, [userData]);

    // Handle status change for anime already in user's list
    const handleStatusChange = async (mediaId, currentStatus, newStatus, animeTitle) => {
        if (newStatus === currentStatus) return;

        if (!token) {
            showError('Authentication required. Please reconnect with OAuth in Settings.', 'Authentication Required');
            return;
        }

        // Optimistic update - update UI immediately
        setOptimisticUpdates(prev => new Map(prev).set(mediaId, newStatus));
        setChangingStatus(prev => new Set([...prev, mediaId]));

        try {
            await updateAnimeStatus(mediaId, newStatus, token);
            console.log(`✅ Updated ${animeTitle} to ${newStatus}`);

            // Update the userAnimeMap
            setUserAnimeMap(prev => new Map(prev).set(mediaId, newStatus));

            // Trigger refresh to get fresh data from AniList
            if (refreshData) {
                refreshData(true);
            }

            setChangingStatus(prev => {
                const next = new Set(prev);
                next.delete(mediaId);
                return next;
            });
        } catch (error) {
            console.error(`❌ Failed to update status for ${animeTitle}:`, error);

            // Revert optimistic update on error
            setOptimisticUpdates(prev => {
                const next = new Map(prev);
                next.delete(mediaId);
                return next;
            });

            setChangingStatus(prev => {
                const next = new Set(prev);
                next.delete(mediaId);
                return next;
            });

            showError(`Failed to update status: ${error.message}`, 'Status Update Failed');
        }
    };

    // Handle adding anime to list (for anime not in user's list)
    const handleAddToList = async (mediaId, status, animeTitle) => {
        if (!token) {
            showError('Authentication required. Please reconnect with OAuth in Settings.', 'Authentication Required');
            return;
        }

        // Optimistic update
        setOptimisticUpdates(prev => new Map(prev).set(mediaId, status));
        setAddingToList(prev => new Set([...prev, mediaId]));

        try {
            const result = await addAnimeWithRetry(mediaId, status, token);
            console.log(`✅ Added ${animeTitle} to list as ${status}`);

            // Update the userAnimeMap to show this anime is now in the list
            setUserAnimeMap(prev => new Map(prev).set(mediaId, status));

            // Trigger refresh to get fresh data from AniList
            if (refreshData) {
                refreshData(true);
            }

            setAddingToList(prev => {
                const next = new Set(prev);
                next.delete(mediaId);
                return next;
            });
        } catch (error) {
            console.error(`❌ Failed to add ${animeTitle} to list:`, error);

            // Revert optimistic update on error
            setOptimisticUpdates(prev => {
                const next = new Map(prev);
                next.delete(mediaId);
                return next;
            });

            setAddingToList(prev => {
                const next = new Set(prev);
                next.delete(mediaId);
                return next;
            });

            showError(`Failed to add to list: ${error.message}`, 'Add Failed');
        }
    };

    const handleFilterChange = (filterKey, value) => {
        setFilters(prev => ({
            ...prev,
            [filterKey]: value
        }));
    };

    const clearFilters = () => {
        setFilters({
            format: '',
            status: '',
            season: '',
            year: ''
        });
        setSearchQuery('');
        setSortBy('none');
        setResults([]);
    };

    const formatAnimeInfo = (anime) => {
        const parts = [];
        if (anime.format) parts.push(anime.format);
        if (anime.episodes) parts.push(`${anime.episodes} eps`);
        if (anime.status) parts.push(anime.status);
        return parts.join(' • ');
    };

    // Prevent body scroll when modal is open
    useEffect(() => {
        if (selectedAnime) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [selectedAnime]);

    // Define dropdown options with colors (no placeholder options - handled by CustomSelect)
    const formatOptions = [
        { value: 'TV', label: 'TV', icon: '📺', color: 'format-tv' },
        { value: 'TV_SHORT', label: 'TV Short', icon: '📺', color: 'format-tv' },
        { value: 'MOVIE', label: 'Movie', icon: '🎬', color: 'format-movie' },
        { value: 'SPECIAL', label: 'Special', icon: '⭐', color: 'format-special' },
        { value: 'OVA', label: 'OVA', icon: '💿', color: 'format-ova' },
        { value: 'ONA', label: 'ONA', icon: '🌐', color: 'format-ona' },
        { value: 'MUSIC', label: 'Music', icon: '🎵', color: 'format-music' },
    ];

    const statusOptions = [
        { value: 'FINISHED', label: 'Finished', icon: '✅', color: 'status-finished' },
        { value: 'RELEASING', label: 'Releasing', icon: '📡', color: 'status-releasing' },
        { value: 'NOT_YET_RELEASED', label: 'Not Yet Released', icon: '📅', color: 'status-upcoming' },
        { value: 'CANCELLED', label: 'Cancelled', icon: '❌', color: 'status-cancelled' },
    ];

    const seasonOptions = [
        { value: 'WINTER', label: 'Winter', icon: '❄️', color: 'season-winter' },
        { value: 'SPRING', label: 'Spring', icon: '🌸', color: 'season-spring' },
        { value: 'SUMMER', label: 'Summer', icon: '☀️', color: 'season-summer' },
        { value: 'FALL', label: 'Fall', icon: '🍂', color: 'season-fall' },
    ];

    return (
        <div className="discover-container">
            <div className="discover-header">
                <h1>🔍 Discover Anime</h1>
            </div>

            {/* Search Box */}
            <div className="search-box">
                <div className="search-input-wrapper">
                    <span className="search-icon">🔍</span>
                    <input
                        type="text"
                        className="search-input"
                        placeholder="Search by title..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        autoFocus
                    />
                    {searchQuery && (
                        <button
                            className="clear-search-btn"
                            onClick={() => setSearchQuery('')}
                            title="Clear search"
                        >
                            ✕
                        </button>
                    )}
                </div>
            </div>

            {/* Filters */}
            <div className="search-filters">
                {/* Sort Controls */}
                <CustomSelect
                    value={sortBy}
                    onChange={(value) => setSortBy(value)}
                    options={sortOptions}
                    placeholder="Sort"
                />

                {sortBy && sortBy !== 'none' && (
                    <button
                        onClick={() => setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')}
                        className="sort-direction-btn"
                        title={sortDirection === 'asc' ? 'Ascending' : 'Descending'}
                    >
                        {sortDirection === 'asc' ? '↑' : '↓'}
                    </button>
                )}

                <CustomSelect
                    value={filters.format}
                    onChange={(value) => handleFilterChange('format', value)}
                    options={formatOptions}
                    placeholder="Format"
                />

                <CustomSelect
                    value={filters.status}
                    onChange={(value) => handleFilterChange('status', value)}
                    options={statusOptions}
                    placeholder="Status"
                />

                <CustomSelect
                    value={filters.season}
                    onChange={(value) => handleFilterChange('season', value)}
                    options={seasonOptions}
                    placeholder="Season"
                />

                <CustomSelect
                    value={filters.year}
                    onChange={(value) => handleFilterChange('year', value)}
                    options={Array.from({ length: new Date().getFullYear() + 2 - 1940 }, (_, i) => {
                        const y = new Date().getFullYear() + 1 - i;
                        return { value: y.toString(), label: y.toString() };
                    })}
                    placeholder="Year"
                    searchable={true}
                />

                {(filters.format || filters.status || filters.season || filters.year || searchQuery || (sortBy && sortBy !== 'none')) && (
                    <button
                        onClick={clearFilters}
                        className="clear-filters-btn"
                        title="Clear all filters"
                    >
                        Clear All
                    </button>
                )}
            </div>


            {/* Results */}
            <div className="search-results">
                {!serverReady && (
                    <div className="no-results">
                        <p>⚠️ Server is not available</p>
                        <p className="no-results-hint">Make sure the server is running (npm run server)</p>
                    </div>
                )}

                {isSearching && results.length === 0 && (
                    <div className="no-results">
                        <p>🔍 Searching...</p>
                    </div>
                )}

                {results.length === 0 && !isSearching && hasSearched && searchQuery.trim().length >= 1 && (
                    <div className="no-results">
                        <p>No anime found matching "{searchQuery}"</p>
                        <p className="no-results-hint">Check the spelling or try different keywords</p>
                    </div>
                )}

                {results.length > 0 && (
                    <>
                        <div className="results-count">
                            Found {results.length} anime{results.length === 100 ? ' (showing top 100)' : ''}
                        </div>
                        <div className="overview-groups-grid">
                            {results.map(anime => {
                                // Calculate watch time (based on episodes * average duration)
                                const episodeDuration = 24; // Average episode duration in minutes
                                const isUnreleased = anime.status === 'NOT_YET_RELEASED';
                                const effectiveEpisodes = isUnreleased ? 0 : (anime.episodes || 0);
                                const totalMinutes = effectiveEpisodes * episodeDuration;
                                const hours = Math.floor(totalMinutes / 60);
                                const mins = totalMinutes % 60;
                                const totalTime = hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;

                                return (
                                    <div
                                        key={anime.id}
                                        className={`overview-group-card ${dropdownOpenAnimeId === anime.id ? 'dropdown-open' : ''}`}
                                        onClick={() => setSelectedAnime(anime)}
                                    >
                                        {anime.coverImage?.large && (
                                            <img
                                                src={anime.coverImage.large}
                                                alt={getPreferredTitle(anime)}
                                                className="overview-cover"
                                                loading="lazy"
                                            />
                                        )}
                                        <div className="overview-group-info">
                                            <h3 className="overview-group-name">
                                                <a
                                                    href={`https://anilist.co/anime/${anime.id}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    onClick={(e) => e.stopPropagation()}
                                                >
                                                    {getPreferredTitle(anime)}
                                                </a>
                                            </h3>
                                            {(() => { const badge = getMediaStatusBadge(anime.status); return <span className={`media-status-badge ${badge.className}`}>{badge.emoji} {badge.label}</span>; })()}

                                            {/* Stats row */}
                                            <div className="overview-stats">
                                                {anime.seasonYear && (
                                                    <span className="stat-year">📅 {anime.seasonYear}</span>
                                                )}
                                                {anime.episodes && !isUnreleased && (
                                                    <>
                                                        {anime.seasonYear && <span>•</span>}
                                                        <span className="stat-episodes">🎬 {anime.episodes} eps</span>
                                                    </>
                                                )}
                                                {anime.averageScore && (
                                                    <>
                                                        <span>•</span>
                                                        <span className="stat-score">⭐ {(anime.averageScore / 10).toFixed(1)}</span>
                                                    </>
                                                )}
                                            </div>

                                            {/* Time stats */}
                                            <div className="overview-time-stats">
                                                <div className="time-stat total">
                                                    <span className="time-label">📺 Total</span>
                                                    <span className="time-value">{totalTime}</span>
                                                </div>
                                            </div>

                                            {/* Genres */}
                                            {anime.genres && anime.genres.length > 0 && (
                                                <div className="anime-genres">
                                                    {anime.genres.slice(0, 3).map((genre, idx) => (
                                                        <span key={idx} className="genre-tag">
                                                            {genre}
                                                        </span>
                                                    ))}
                                                </div>
                                            )}

                                            {/* Status Button Section */}
                                            <div className="discover-status-section" onClick={(e) => e.stopPropagation()}>
                                                {(() => {
                                                    const animeTitle = getPreferredTitle(anime);
                                                    const currentStatus = userAnimeMap.get(anime.id);
                                                    const optimisticStatus = optimisticUpdates.get(anime.id);
                                                    const displayStatus = optimisticStatus || currentStatus;
                                                    const isChanging = changingStatus.has(anime.id);
                                                    const isAdding = addingToList.has(anime.id);

                                                    // Anime is in user's list - show status button
                                                    if (currentStatus || optimisticStatus) {
                                                        return (
                                                            <StatusButton
                                                                currentStatus={displayStatus}
                                                                onChange={(newStatus) => handleStatusChange(anime.id, displayStatus, newStatus, animeTitle)}
                                                                disabled={isChanging}
                                                                isLoading={isChanging}
                                                            />
                                                        );
                                                    }

                                                    // Anime is NOT in user's list - show "Add to List" button
                                                    return (
                                                        <AddToListButton
                                                            onAdd={(status) => handleAddToList(anime.id, status, animeTitle)}
                                                            isAdding={isAdding}
                                                            onOpenChange={(isOpen) => setDropdownOpenAnimeId(isOpen ? anime.id : null)}
                                                        />
                                                    );
                                                })()}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </>
                )}
            </div>

            {/* Anime Details Modal */}
            {selectedAnime && (
                <div className="anime-modal-overlay" onClick={() => setSelectedAnime(null)}>
                    <div className="anime-modal" onClick={(e) => e.stopPropagation()}>
                        <button
                            className="modal-close-btn"
                            onClick={() => setSelectedAnime(null)}
                        >
                            ✕
                        </button>

                        {selectedAnime.bannerImage && (
                            <img
                                src={selectedAnime.bannerImage}
                                alt=""
                                className="anime-banner"
                            />
                        )}

                        <div className="modal-content">
                            <div className="modal-header">
                                {selectedAnime.coverImage?.large && (
                                    <img
                                        src={selectedAnime.coverImage.large}
                                        alt={getPreferredTitle(selectedAnime)}
                                        className="modal-cover"
                                    />
                                )}
                                <div className="modal-header-info">
                                    <h2>
                                        <a
                                            href={`https://anilist.co/anime/${selectedAnime.id}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            {getPreferredTitle(selectedAnime)}
                                        </a>
                                    </h2>
                                    {selectedAnime.title.english && selectedAnime.title.english !== selectedAnime.title.romaji && (
                                        <p className="alt-title">{selectedAnime.title.english}</p>
                                    )}
                                    <p className="anime-meta">{formatAnimeInfo(selectedAnime)}</p>
                                    {selectedAnime.averageScore && (
                                        <div className="score-large">
                                            ⭐ {selectedAnime.averageScore / 10}/10
                                        </div>
                                    )}
                                </div>
                            </div>

                            {selectedAnime.description && (
                                <div className="modal-description">
                                    <h3>Description</h3>
                                    <div
                                        dangerouslySetInnerHTML={{
                                            __html: selectedAnime.description
                                        }}
                                    />
                                </div>
                            )}

                            {selectedAnime.genres && selectedAnime.genres.length > 0 && (
                                <div className="modal-genres">
                                    <h3>Genres</h3>
                                    <div className="genre-list">
                                        {selectedAnime.genres.map((genre, idx) => (
                                            <span key={idx} className="genre-tag-large">
                                                {genre}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            )}

                            <div className="modal-actions">
                                <a
                                    href={`https://anilist.co/anime/${selectedAnime.id}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="btn-anilist"
                                >
                                    View on AniList
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Search;
